import { PaginatedResponse } from "@elit-hair/elit-tools";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { callSvcAxiosInstance } from "@/constants";
import { teamServiceEndpoints } from "@/constants/endpoints";
import { DEFAULT_EMPTY_PAGINATION } from "@/constants/pagination";
import { QueryOptions } from "@/types/pagination.types";
import { CreateTeamRequest, Team, TeamMember, TeamRole, UpdateTeamRequest } from "@/types/team.types";
import { enrichTeamsWithUsers } from "@/utils/enrich-teams-with-users";
import { sortTeamsBySortOrder } from "@/utils/team.utils";

export const teamKeys = {
    all: ["teams"] as const,
    lists: () => [...teamKeys.all, "list"] as const,
    list: (filters?: Record<string, unknown>) => [...teamKeys.lists(), { filters }] as const,
    details: () => [...teamKeys.all, "detail"] as const,
    detail: (id: string) => [...teamKeys.details(), id] as const,
    members: () => [...teamKeys.all, "members"] as const,
    teamMembers: (teamId: string) => [...teamKeys.members(), teamId] as const,
};

// API functions
const getTeamsQuery = async (): Promise<PaginatedResponse<Team>> => {
    const response = await callSvcAxiosInstance.get<PaginatedResponse<Team>>(teamServiceEndpoints.getTeams);
    const enrichedData = await enrichTeamsWithUsers(response.data.data);
    const teams = Array.isArray(enrichedData) ? enrichedData : [enrichedData];
    return {
        ...response.data,
        data: sortTeamsBySortOrder(teams),
    };
};

const getTeamsPaginatedQuery = async (options: QueryOptions = {}): Promise<PaginatedResponse<Team>> => {
    // Remove pageSize if it's undefined
    const params = { ...options };
    if (params.pageSize === undefined) {
        delete params.pageSize;
    }
    const response = await callSvcAxiosInstance.get<PaginatedResponse<Team>>(teamServiceEndpoints.getTeams, {
        params,
    });
    const enrichedData = await enrichTeamsWithUsers(response.data.data);
    const teams = Array.isArray(enrichedData) ? enrichedData : [enrichedData];
    return {
        ...response.data,
        data: sortTeamsBySortOrder(teams),
    };
};

const getTeamQuery = async (teamId: string): Promise<Team> => {
    const response = await callSvcAxiosInstance.get<Team>(teamServiceEndpoints.getTeamById(teamId));
    const enrichedTeam = await enrichTeamsWithUsers(response.data);
    return Array.isArray(enrichedTeam) ? enrichedTeam[0] : enrichedTeam;
};

const createTeamMutation = async (teamData: CreateTeamRequest): Promise<Team> => {
    const response = await callSvcAxiosInstance.post<Team>(teamServiceEndpoints.createTeam, teamData);
    const enrichedTeam = await enrichTeamsWithUsers(response.data);
    return Array.isArray(enrichedTeam) ? enrichedTeam[0] : enrichedTeam;
};

const updateTeamMutation = async ({
    teamId,
    teamData,
}: {
    teamId: string;
    teamData: UpdateTeamRequest;
}): Promise<Team> => {
    // Filter out members without a valid role before sending to backend
    if (Array.isArray(teamData.members)) {
        teamData.members = teamData.members.filter(
            (member) => member.role === TeamRole.TEAM_LEAD || member.role === TeamRole.AGENT
        );
    }
    const response = await callSvcAxiosInstance.patch<Team>(teamServiceEndpoints.updateTeam(teamId), teamData);
    const enrichedTeam = await enrichTeamsWithUsers(response.data);
    return Array.isArray(enrichedTeam) ? enrichedTeam[0] : enrichedTeam;
};

const deleteTeamMutation = async (teamId: string): Promise<void> => {
    await callSvcAxiosInstance.delete(teamServiceEndpoints.deleteTeam(teamId));
};

const getTeamMembersQuery = async (teamId?: string): Promise<TeamMember[]> => {
    if (teamId) {
        const response = await callSvcAxiosInstance.get<Team>(teamServiceEndpoints.getTeamById(teamId));
        const enrichedTeam = await enrichTeamsWithUsers(response.data);
        const team = Array.isArray(enrichedTeam) ? enrichedTeam[0] : enrichedTeam;
        return team.members;
    }

    // Get all team members from all teams
    const response = await callSvcAxiosInstance.get<PaginatedResponse<Team>>(teamServiceEndpoints.getTeams);
    const enrichedData = await enrichTeamsWithUsers(response.data.data);
    const teams = Array.isArray(enrichedData) ? enrichedData : [enrichedData];
    const members: TeamMember[] = teams.map((team) => team.members).flat();

    // Remove duplicates based on member id and sort alphabetically by user name
    const uniqueMembers = members.filter((member, index, self) => index === self.findIndex((m) => m.id === member.id));

    const sortedMembers = uniqueMembers.sort((a, b) => {
        const nameA = a.user?.name || "";
        const nameB = b.user?.name || "";
        return nameA.localeCompare(nameB);
    });

    return sortedMembers;
};

// Query hooks
export const useTeams = () => {
    return useQuery<PaginatedResponse<Team>>({
        queryKey: teamKeys.list(),
        queryFn: getTeamsQuery,
    });
};

export const useTeamsPaginated = (options: QueryOptions = {}) => {
    const defaultTeamsResponse: PaginatedResponse<Team> = {
        data: [],
        pagination: DEFAULT_EMPTY_PAGINATION,
    };

    const query = useQuery({
        queryKey: teamKeys.list(options),
        queryFn: () => getTeamsPaginatedQuery(options),
    });

    return {
        ...query,
        data: query.data ?? defaultTeamsResponse,
    };
};

export const useTeam = (teamId: string) => {
    return useQuery({
        queryKey: teamKeys.detail(teamId),
        queryFn: () => getTeamQuery(teamId),
        enabled: !!teamId,
    });
};

export const useTeamMembers = (teamId?: string) => {
    return useQuery({
        queryKey: teamId ? teamKeys.teamMembers(teamId) : teamKeys.members(),
        queryFn: () => getTeamMembersQuery(teamId),
    });
};

// Mutation hooks
export const useCreateTeam = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createTeamMutation,
        onSuccess: () => {
            // Invalidate and refetch teams list
            queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
        },
    });
};

export const useUpdateTeam = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateTeamMutation,
        onSuccess: (updatedTeam) => {
            // Invalidate and refetch teams list
            queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
            // Update specific team query cache
            queryClient.setQueryData(teamKeys.detail(updatedTeam.id), updatedTeam);
        },
    });
};

export const useDeleteTeam = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteTeamMutation,
        onSuccess: () => {
            // Invalidate and refetch teams list
            queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
        },
    });
};
